import { useMemo } from 'react';
import type { Modality, TrialTarget, VisualSide } from '../types';
import { makeColorPair, makeContrastPaint } from '../lib/color';

export interface StimulusDefinition {
  modality: Modality;
  index: number;
  delta: number;
  target: TrialTarget;
  base: Record<string, number | string>;
  colorReference?: string;
  colorAltered?: string;
  contrastBase?: string;
  contrastSignal?: string;
  pitchBaseHz?: number;
}

export function createStimulus(modality: Modality, index: number, delta: number): StimulusDefinition {
  if (modality === 'pitch') {
    const target = Math.random() > 0.5 ? 'first' : 'second';
    const baseHz = 220 + Math.round(Math.random() * 420);
    return { modality, index, delta, target, pitchBaseHz: baseHz, base: { baseHz, target } };
  }
  const target: VisualSide = Math.random() > 0.5 ? 'left' : 'right';
  if (modality === 'color') {
    const pair = makeColorPair(delta);
    return {
      modality,
      index,
      delta,
      target,
      colorReference: pair.reference,
      colorAltered: pair.altered,
      base: pair.meta
    };
  }
  const paint = makeContrastPaint(delta);
  return {
    modality,
    index,
    delta,
    target,
    contrastBase: paint.base,
    contrastSignal: paint.signal,
    base: paint.meta
  };
}

interface Props {
  stimulus: StimulusDefinition;
  disabled?: boolean;
  onAnswer: (answer: TrialTarget) => void;
  onReplayPitch: () => void;
}

export function Stimulus({ stimulus, disabled, onAnswer, onReplayPitch }: Props) {
  if (stimulus.modality === 'pitch') {
    return (
      <section className="stimulus-card pitch-card" aria-label="Pitch discrimination task">
        <div className="tone-orbit" aria-hidden="true">
          <span className="tone-dot tone-dot-a" />
          <span className="tone-dot tone-dot-b" />
          <span className="tone-ring" />
        </div>
        <div className="prompt-block">
          <p className="eyebrow">two intervals · one is higher</p>
          <h2>Which tone climbed?</h2>
          <p className="hint">Use headphones if you can. Keyboard: <kbd>1</kbd> or <kbd>2</kbd>.</p>
        </div>
        <div className="answer-row">
          <button disabled={disabled} className="choice-button" onClick={() => onAnswer('first')}>1 · First tone higher</button>
          <button disabled={disabled} className="choice-button" onClick={() => onAnswer('second')}>2 · Second tone higher</button>
        </div>
        <button disabled={disabled} className="ghost-button" onClick={onReplayPitch}>Replay pair</button>
      </section>
    );
  }

  if (stimulus.modality === 'color') {
    const leftColor = stimulus.target === 'left' ? stimulus.colorAltered : stimulus.colorReference;
    const rightColor = stimulus.target === 'right' ? stimulus.colorAltered : stimulus.colorReference;
    return (
      <section className="stimulus-card" aria-label="Color discrimination task">
        <div className="prompt-block">
          <p className="eyebrow">reference in the center</p>
          <h2>Which side is not the same color?</h2>
          <p className="hint">Keyboard: <kbd>←</kbd> or <kbd>→</kbd>. Trust the first shimmer you see.</p>
        </div>
        <div className="color-stage">
          <button disabled={disabled} className="swatch choice-swatch" style={{ background: leftColor }} onClick={() => onAnswer('left')} aria-label="Left color is different" />
          <div className="reference-column">
            <span className="reference-label">reference</span>
            <span className="reference-swatch" style={{ background: stimulus.colorReference }} />
          </div>
          <button disabled={disabled} className="swatch choice-swatch" style={{ background: rightColor }} onClick={() => onAnswer('right')} aria-label="Right color is different" />
        </div>
      </section>
    );
  }

  return <ContrastStimulus stimulus={stimulus} disabled={disabled} onAnswer={onAnswer} />;
}

function ContrastStimulus({ stimulus, disabled, onAnswer }: Omit<Props, 'onReplayPitch'>) {
  const makeStyle = useMemo(() => {
    return (side: VisualSide) => {
      const isSignal = stimulus.target === side;
      const base = stimulus.contrastBase ?? '#151520';
      const signal = stimulus.contrastSignal ?? '#232334';
      return {
        backgroundColor: base,
        backgroundImage: isSignal
          ? `radial-gradient(circle at 50% 50%, transparent 0 18%, ${signal} 19% 22%, transparent 23% 100%), linear-gradient(135deg, rgba(255,255,255,.015), rgba(255,255,255,0))`
          : `linear-gradient(135deg, rgba(255,255,255,.015), rgba(255,255,255,0))`
      } as React.CSSProperties;
    };
  }, [stimulus]);

  return (
    <section className="stimulus-card" aria-label="Contrast detection task">
      <div className="prompt-block">
        <p className="eyebrow">barely visible signal</p>
        <h2>Which panel holds the ghost ring?</h2>
        <p className="hint">Keyboard: <kbd>←</kbd> or <kbd>→</kbd>. Your display brightness matters.</p>
      </div>
      <div className="contrast-stage">
        <button disabled={disabled} className="contrast-panel" style={makeStyle('left')} onClick={() => onAnswer('left')} aria-label="Left panel has the signal" />
        <button disabled={disabled} className="contrast-panel" style={makeStyle('right')} onClick={() => onAnswer('right')} aria-label="Right panel has the signal" />
      </div>
    </section>
  );
}
